import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../shared/utils/cn';
import { useModal } from './hooks/useModal';
import { ModalProvider } from './context';
import { ModalHeader, ModalBody, ModalFooter } from './components';
import { ModalProps } from '@/shared/types/modal';
import { SIZE_MAP } from './const';
import { findModalHeader, findModalFooter } from './utils/utils';
import { createModalCompound } from './Modal.compound';

/**
 * Modal - Componente de modal/diálogo versátil y configurable
 *
 * Componente de modal que soporta múltiples tamaños, botones personalizados, tabs, iconos,
 * badges, y un sistema de compound components para máxima flexibilidad.
 *
 * @example
 * ```tsx
 * // Modal simple
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirmar acción"
 * >
 *   <Modal.Body>¿Estás seguro de que deseas continuar?</Modal.Body>
 *   <Modal.Footer>
 *     <Button onClick={() => setIsOpen(false)}>Cancelar</Button>
 *     <Button onClick={handleConfirm}>Confirmar</Button>
 *   </Modal.Footer>
 * </Modal>
 *
 * // Modal con tabs
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   tabs={[
 *     { label: 'Tab 1', value: 'tab1' },
 *     { label: 'Tab 2', value: 'tab2' }
 *   ]}
 * >
 *   <Modal.Body>Contenido del modal</Modal.Body>
 * </Modal>
 * ```
 *
 * @see {@link ModalProps} para la lista completa de props
 * @see {@link Modal.Header}, {@link Modal.Body}, {@link Modal.Footer} para compound components
 */
// eslint-disable-next-line react-refresh/only-export-components
const ModalComponent: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  icon,
  size = 'md',
  children,
  showCloseButton = true,
  disableEscapeClose = false,
  disableOutsideTab = true,
  disableOutsideClick = false,
  closeAtCorner = true,
  cancelButtonText,
  successButtonText,
  onCancel,
  onSuccess,
  title,
  zIndex = 52,
  showCancelButton,
  showSuccessButton,
  closeAtSuccess,
  closeAtCancel,
  tabs,
  tabsConfig,
  defaultValueTab,
  onChangeTab,
  headerButtonIcon,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  headerButtonIconDuotonePrimary,
  headerButtonIconDuotoneSecondary,
  headerButtonIconDuotoneOpacityPrimary,
  headerButtonIconDuotoneOpacitySecondary,
  headerButtonOnClick,
  footerButtonIcon,
  footerButtonIconDuotonePrimary,
  footerButtonIconDuotoneSecondary,
  footerButtonIconDuotoneOpacityPrimary,
  footerButtonIconDuotoneOpacitySecondary,
  footerButtonOnClick,
  cancelButtonSlotLeft,
  successButtonSlotRight,
  disableCancelButton,
  disableSuccessButton,
  successButtonColor,
  footerSlotLayout,
  badgeIcon,
  badgeIconDuotonePrimary,
  badgeIconDuotoneSecondary,
  badgeIconDuotoneOpacityPrimary,
  badgeIconDuotoneOpacitySecondary,
  badgeLabel,
  hideFooter = false,
  scrollMode = 'paper',
  // Nuevo sistema (modelo cerrado)
  iconConfig,
  closeButton,
  headerButton,
  headerButtons,
  badge,
  badges,
  footerAuxButton,
  footerPrimaryButton,
  footerSecondaryButton,
  footerButtons,
}) => {
  const { isVisible, animateIn, container, modalRef } = useModal({
    isOpen,
    onClose,
    disableEscapeClose,
    disableOutsideTab,
    disableOutsideClick,
  });
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const isPaper = scrollMode === 'paper';
  /** Si el panel supera la altura útil del viewport, alineamos arriba para que el scroll muestre el inicio; si no, centramos. Solo aplica en modo scroll. */
  const [alignPanelTop, setAlignPanelTop] = useState(false);

  const updateVerticalAlign = useCallback(() => {
    if (isPaper) return;
    const panel = modalRef.current;
    if (!panel) return;
    const vh = window.innerHeight;
    const verticalPaddingTotal = 64;
    setAlignPanelTop(panel.offsetHeight + verticalPaddingTotal > vh);
  }, [modalRef, isPaper]);

  useLayoutEffect(() => {
    if (!isVisible || isPaper) {
      setAlignPanelTop(false);
      return;
    }
    updateVerticalAlign();
    const panel = modalRef.current;
    if (!panel) return;
    const ro = new ResizeObserver(updateVerticalAlign);
    ro.observe(panel);
    window.addEventListener('resize', updateVerticalAlign);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateVerticalAlign);
    };
  }, [isVisible, updateVerticalAlign, modalRef, isPaper]);

  useLayoutEffect(() => {
    if (!isVisible || isPaper) return;
    const root = scrollRootRef.current;
    if (!root) return;
    root.scrollTop = 0;
  }, [isOpen, isVisible, alignPanelTop, isPaper]);

  if (!container || (!isOpen && !isVisible)) return null;

  // Check if children include a Modal.Header
  const hasCustomHeader = findModalHeader(children);
  const hasCustomFooter = findModalFooter(children);
  const modalRootZClass =
    zIndex === 60 ? 'ui-z-[60]' : zIndex === 52 ? 'ui-z-[52]' : zIndex === 50 ? 'ui-z-50' : '';
  const modalRootStyle = zIndex === 50 || zIndex === 52 || zIndex === 60 ? undefined : { zIndex };

  const modalContent = (
    <div
      aria-modal="true"
      role="dialog"
      aria-describedby="modal-description"
      ref={isPaper ? undefined : scrollRootRef}
      className={cn(
        'ui-imj',
        'modal-portal',
        'ui-fixed ui-inset-0',
        isPaper ? 'ui-overflow-hidden' : 'ui-overflow-y-auto',
        modalRootZClass
      )}
      style={modalRootStyle}
    >
      {/* Overlay */}
      <div
        className={cn(
          'ui-fixed ui-inset-0 ui-bg-black ui-backdrop-blur-sm ui-transition-opacity ui-duration-200',
          animateIn ? 'ui-opacity-40' : 'ui-opacity-0'
        )}
        onClick={disableOutsideClick ? (e) => e.stopPropagation() : onClose}
      />

      {/* Modal Container */}
      <div
        className={cn(
          'ui-flex ui-justify-center ui-px-4',
          isPaper
            ? 'ui-h-full ui-items-center ui-py-[16px]'
            : cn('ui-min-h-full ui-py-8', alignPanelTop ? 'ui-items-start' : 'ui-items-center')
        )}
      >
        {/* Panel */}
        <div
          ref={modalRef}
          tabIndex={-1}
          className={cn(
            'ui-relative ui-z-10 ui-flex ui-w-full ui-flex-col ui-rounded-surface-modal ui-border-1 ui-border-default ui-bg-fill ui-shadow-02 ui-outline-none',
            isPaper && 'ui-max-h-[calc(100svh-32px)] ui-overflow-hidden',
            SIZE_MAP[size]
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={cn(
              'ui-flex ui-min-h-0 ui-w-full ui-transform ui-flex-col ui-transition-all ui-duration-200',
              animateIn
                ? 'ui-translate-y-0 ui-scale-100 ui-opacity-100'
                : 'ui-translate-y-4 ui-scale-95 ui-opacity-0'
            )}
          >
            <ModalProvider
              isOpen={isOpen}
              onClose={onClose}
              showCloseButton={showCloseButton}
              closeAtCorner={closeAtCorner}
              cancelButtonText={cancelButtonText}
              successButtonText={successButtonText}
              onCancel={onCancel}
              onSuccess={onSuccess}
              showCancelButton={showCancelButton}
              showSuccessButton={showSuccessButton}
              closeAtSuccess={closeAtSuccess}
              closeAtCancel={closeAtCancel}
              title={title}
              icon={icon}
              iconDuotonePrimary={iconDuotonePrimary}
              iconDuotoneSecondary={iconDuotoneSecondary}
              iconDuotoneOpacityPrimary={iconDuotoneOpacityPrimary}
              iconDuotoneOpacitySecondary={iconDuotoneOpacitySecondary}
              headerButtonIcon={headerButtonIcon}
              headerButtonIconDuotonePrimary={headerButtonIconDuotonePrimary}
              headerButtonIconDuotoneSecondary={headerButtonIconDuotoneSecondary}
              headerButtonIconDuotoneOpacityPrimary={headerButtonIconDuotoneOpacityPrimary}
              headerButtonIconDuotoneOpacitySecondary={headerButtonIconDuotoneOpacitySecondary}
              headerButtonOnClick={headerButtonOnClick}
              footerButtonIcon={footerButtonIcon}
              footerButtonIconDuotonePrimary={footerButtonIconDuotonePrimary}
              footerButtonIconDuotoneSecondary={footerButtonIconDuotoneSecondary}
              footerButtonIconDuotoneOpacityPrimary={footerButtonIconDuotoneOpacityPrimary}
              footerButtonIconDuotoneOpacitySecondary={footerButtonIconDuotoneOpacitySecondary}
              footerButtonOnClick={footerButtonOnClick}
              tabs={tabs}
              tabsConfig={tabsConfig}
              defaultValueTab={defaultValueTab}
              onChangeTab={onChangeTab}
              cancelButtonSlotLeft={cancelButtonSlotLeft}
              successButtonSlotRight={successButtonSlotRight}
              disableCancelButton={disableCancelButton}
              disableSuccessButton={disableSuccessButton}
              successButtonColor={successButtonColor}
              footerSlotLayout={footerSlotLayout}
              badgeIcon={badgeIcon}
              badgeIconDuotonePrimary={badgeIconDuotonePrimary}
              badgeIconDuotoneSecondary={badgeIconDuotoneSecondary}
              badgeIconDuotoneOpacityPrimary={badgeIconDuotoneOpacityPrimary}
              badgeIconDuotoneOpacitySecondary={badgeIconDuotoneOpacitySecondary}
              badgeLabel={badgeLabel}
              hideFooter={hideFooter}
              scrollMode={scrollMode}
              iconConfig={iconConfig}
              closeButton={closeButton}
              headerButton={headerButton}
              headerButtons={headerButtons}
              badge={badge}
              badges={badges}
              footerAuxButton={footerAuxButton}
              footerPrimaryButton={footerPrimaryButton}
              footerSecondaryButton={footerSecondaryButton}
              footerButtons={footerButtons}
            >
              {!hasCustomHeader &&
                (showCloseButton || title ? <ModalHeader title={title} /> : null)}
              {children && children}
              {!hasCustomFooter && !hideFooter && <ModalFooter />}
            </ModalProvider>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, container);
};

// Create the compound component
export const Modal = createModalCompound(ModalComponent);
